import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./ItemDetails.module.css";
import ItemThirdParty from "Shared/ItemThirdParty";
import { getPriceLabel } from "../../utils/getPriceLabel";
import {
  formatRetailPrice,
  RETAIL_PRICE_LABEL,
} from "../../utils/formatRetailPrice";

export function ItemDetails({
  stockInfo,
  detailsPage = true,
  is3rdParty = false,
  onQuantityChange,
  initialQty = 1,
  stockStatus,
  actions,
}) {
  const [qty, setQty] = useState(initialQty);
  const author = stockInfo.author || stockInfo.creator;
  const retailPriceText = formatRetailPrice(stockInfo.retailPrice);

  const setAndNotify = (n) => {
    const val = Math.max(1, n);
    setQty(val);
    onQuantityChange && onQuantityChange(val);
  };

  return (
    <div className={styles.container}>
      <div className={styles.titleRow}>
        <span className={styles.title}>{stockInfo.title}</span>
        {!detailsPage && (
          <span className={styles.skuRight}>
            {stockInfo.sku}
            {is3rdParty && <ItemThirdParty is3rdParty={is3rdParty} />}
          </span>
        )}
      </div>

      <div className={styles.metaColumns}>
        <div className={styles.leftColumn}>
          <div className={styles.metaRow}>
            <span className={styles.metaLabelBlue}>
              {getPriceLabel(stockInfo.itemType)}:
            </span>
            <span className={styles.metaValue}>
              $
              {Number(
                stockInfo.memberPrice ?? stockInfo.retailPrice ?? 0
              ).toFixed(2)}
            </span>
          </div>

          {retailPriceText && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabelBlue}>
                {RETAIL_PRICE_LABEL}:
              </span>
              <span className={styles.metaValue}>{retailPriceText}</span>
            </div>
          )}

          {author && (
            <div className={classNames(styles.metaRow, styles.authorRow)}>
              <span className={styles.authorLabel}>Author:</span>
              <span className={styles.metaValue}>{author}</span>
            </div>
          )}

          {stockInfo.color && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabelGold}>Color:</span>
              <span className={styles.metaValue}>{stockInfo.color}</span>
            </div>
          )}
          {stockInfo.size && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabelGold}>Size:</span>
              <span className={styles.metaValue}>{stockInfo.size}</span>
            </div>
          )}

          {stockStatus && <div className={styles.metaRow}>{stockStatus}</div>}
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.quantityBlock}>
            <span className={styles.quantityLabel}>Quantity</span>
            <div className={styles.stepper} aria-label="Quantity selector">
              <div className={styles.value} role="status" aria-live="polite">
                {qty}
              </div>
              <div className={styles.chevrons}>
                <button
                  type="button"
                  className={styles.chev}
                  aria-label="Increase quantity"
                  onClick={() => setAndNotify(qty + 1)}
                >
                  <svg viewBox="0 0 24 24" className={styles.chevSvg}>
                    <path
                      d="M7 14l5-5 5 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  className={styles.chev}
                  aria-label="Decrease quantity"
                  onClick={() => setAndNotify(qty - 1)}
                >
                  <svg viewBox="0 0 24 24" className={styles.chevSvg}>
                    <path
                      d="M7 10l5 5 5-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      </div>
    </div>
  );
}

ItemDetails.propTypes = {
  stockInfo: PropTypes.object.isRequired,
  detailsPage: PropTypes.bool,
  is3rdParty: PropTypes.bool,
  onQuantityChange: PropTypes.func,
  initialQty: PropTypes.number,
  stockStatus: PropTypes.node,
  actions: PropTypes.node,
};

export default ItemDetails;
